import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import {
  useGetGradeSystemQuery,
  useGetStudentReportQuery,
  useGetSubjectForClassesQuery,
  useGetClassroomsQuery,
  useSetGradeMutation,
  useUpdateStudentReportMutation,
  useSaveTraitEvaluationMutation,
} from "../hooks/useAdmin";
import { CircleLoader } from "../components/CircleLoader";
import {
  generateAcademicSessions,
  getCurrentAcademicSession,
  getCurrentAcademicTerm,
} from "../utilities/academicSession";
import {
  FaGraduationCap,
  FaBookmark,
  FaCalendarAlt,
  FaStar,
  FaPenNib,
  FaCommentMedical,
  FaClipboardList,
  FaInfoCircle,
  FaCheckCircle,
  FaChild,
  FaLayerGroup,
} from "react-icons/fa";

export const GradesEntry = () => {
  const { studentid, classid } = useParams();
  const { data: classSubjects } = useGetSubjectForClassesQuery();
  const { data: classroomsData } = useGetClassroomsQuery();
  const { data: gradez } = useGetGradeSystemQuery();
  const location = useLocation();
  const {
    student_name,
    studentPix,
    classroom: navClassroom,
    assessment_mode: navMode,
  } = location.state || {
    student_name: "Student",
    studentPix: "",
  };
  const setGrade = useSetGradeMutation();
  const updateReport = useUpdateStudentReportMutation();
  const user = JSON.parse((sessionStorage.getItem("user") as string) || "{}");

  const [term, setTerm] = useState<number | "">(getCurrentAcademicTerm());
  const [academicSession, setAcademicSession] = useState<string>(
    getCurrentAcademicSession(),
  );
  const sessionOptions = React.useMemo(
    () => generateAcademicSessions(2025),
    [],
  );

  const { data: reports } = useGetStudentReportQuery(
    studentid ?? "",
    term,
    academicSession,
  );

  const reportData = reports?.data?.data || reports?.data;

  // Resolve classroom entity from navigation state, classSubjects, classroomsData, or reportData
  const selectedClass =
    navClassroom ||
    (classSubjects?.data || (Array.isArray(classSubjects) ? classSubjects : []))?.find(
      (cls: any) => String(cls.id) === String(classid),
    ) ||
    (classroomsData?.data || (Array.isArray(classroomsData) ? classroomsData : []))?.find(
      (cls: any) => String(cls.id) === String(classid),
    ) ||
    reportData?.classroom ||
    reportData?.student?.classroom;

  // Auto-detect mode from classroom config: traits_only vs standard_matrix
  const detectedMode =
    navMode ||
    selectedClass?.assessment_mode ||
    selectedClass?.assessment_type ||
    reportData?.assessment_mode ||
    reportData?.assessment_type;

  const isTraitsOnly =
    detectedMode === "traits_only"
      ? true
      : detectedMode === "standard_matrix" || detectedMode === "standard"
      ? false
      : !selectedClass?.subjects || selectedClass?.subjects?.length === 0;

  const assessmentMode: "standard" | "traits_only" = isTraitsOnly
    ? "traits_only"
    : "standard";

  const reportSubjects = React.useMemo(() => {
    if (!reportData?.grades?.length) return [];
    return reportData.grades
      .map((g: any) => g.subject)
      .filter((s: any) => s && s.id);
  }, [reportData]);

  const subjects = selectedClass?.subjects?.length
    ? selectedClass.subjects
    : reportSubjects;

  const grading = gradez?.data?.[0];
  const numCAs = grading?.num_cas ?? 3;
  const caWeight = grading?.ca_weight ?? 0;
  const examWeight = grading?.exam_weight ?? 0;
  const [teacherRemark, setTeacherRemark] = useState("");
  const [adminRemark, setAdminRemark] = useState("");

  const ratingTraits = React.useMemo(() => {
    return typeof grading?.extracurricular_traits === "string"
      ? JSON.parse(grading?.extracurricular_traits)
      : grading?.extracurricular_traits || [];
  }, [grading?.extracurricular_traits]);

  const [scores, setScores] = useState<{ [subjectId: string]: any }>({});
  const [ratings, setRatings] = useState<{ [key: string]: number }>({});
  const [attendanceCount, setAttendanceCount] = useState<number | "">("");
  const [teacherRemarks, setTeacherRemarks] = useState<string>("");
  const [adminRemarks, setAdminRemarks] = useState<string>("");

  const [remarkModal, setRemarkModal] = useState<{
    open: boolean;
    subject: string | null;
  }>({ open: false, subject: null });

  const isViewOnly = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    if (reportData) {
      setTeacherRemark(reportData.teacher_remarks || "");
      setAdminRemark(reportData.admin_remarks || "");
    }
  }, [reportData]);

  /** 🧩 Prefill Data */
  useEffect(() => {
    if (reportData) {
      const prefillScores: { [subjectId: string]: any } = {};
      const prefillRatings: { [key: string]: number } = {};

      const gradesList = reportData.grades || [];
      if (gradesList.length) {
        gradesList.forEach((g: any) => {
          const subjId = g.subject?.id || g.subject_id || g.id;
          const gradeItem = Array.isArray(g.grades)
            ? (term
              ? g.grades.find((gr: any) => Number(gr.term) === Number(term))
              : null) ||
            g.grades[0] ||
            g
            : g;

          if (subjId) {
            prefillScores[subjId] = {
              grade_id: gradeItem?.id,
              ca: gradeItem?.ca_scores || g.ca_scores || {},
              exam:
                gradeItem?.exam_score !== undefined &&
                  gradeItem?.exam_score !== null
                  ? gradeItem.exam_score
                  : g.exam_score ?? "",
              remark: gradeItem?.remarks || g.remarks || "",
            };
          }
        });
      }

      const backendRatings =
        reportData.psychomotor_skills ||
        reportData.evaluation?.psychomotor_skills ||
        {};

      if (backendRatings && Object.keys(backendRatings).length > 0) {
        Object.entries(backendRatings).forEach(([trait, val]) => {
          prefillRatings[trait] = Number(val);
        });
      } else {
        ratingTraits.forEach((trait: any) => {
          prefillRatings[trait.name] = 0;
        });
      }

      setScores(prefillScores);
      setRatings(prefillRatings);
      setTeacherRemarks(
        reportData.teacher_remarks ||
        reportData.evaluation?.teacher_remarks ||
        "",
      );
      setAdminRemarks(
        reportData.admin_remarks ||
        reportData.evaluation?.admin_remarks ||
        "",
      );

      const count =
        reportData.summary?.loginCount ??
        reportData.summary?.login_count ??
        reportData.summary?.attendance_count ??
        reportData.attendance_count;
      if (count !== undefined && count !== null) {
        setAttendanceCount(Number(count));
      }

      if (!term && gradesList[0]?.grades?.[0]?.term) {
        setTerm(gradesList[0].grades[0].term);
      }
      if (!academicSession && reportData.academic_session) {
        setAcademicSession(reportData.academic_session);
      }
    }
  }, [reportData, ratingTraits]);

  /** 🧩 CA / Exam Handlers with dynamic clearing fix */
  const handleCAScoreChange = (
    subjectId: string,
    index: number,
    value: string,
  ) => {
    setScores((prev) => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        ca: {
          ...prev[subjectId]?.ca,
          [index]: value === "" ? "" : Number(value),
        },
      },
    }));
  };

  const handleExamChange = (subjectId: string, value: string) => {
    setScores((prev) => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        exam: value === "" ? "" : Number(value),
      },
    }));
  };

  const handleRemarkSave = (subjectId: string, remark: string) => {
    setScores((prev) => ({
      ...prev,
      [subjectId]: { ...prev[subjectId], remark },
    }));
    setRemarkModal({ open: false, subject: null });
  };

  const handleRatingChange = (trait: string, value: number) => {
    if (isViewOnly) return;
    setRatings((prev) => ({ ...prev, [trait]: value }));
  };

  const handleSetAllRatings = (val: number) => {
    if (isViewOnly) return;
    const updated: { [key: string]: number } = {};
    ratingTraits.forEach((trait: any) => {
      updated[trait.name] = val;
    });
    setRatings(updated);
  };

  const calcTotal = (subjectId: string) => {
    const subj = scores[subjectId];
    if (!subj) return 0;
    const totalCA = Object.values(subj.ca || {}).reduce(
      (sum: number, v) => sum + Number(v || 0),
      0,
    );
    return totalCA + Number(subj.exam || 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!term || !academicSession) {
      alert("⚠️ Please select term and session.");
      return;
    }

    const countVal = attendanceCount === "" ? 0 : Number(attendanceCount);

    // If in traits-only mode (e.g., nursery, early years, or trait classes)
    if (assessmentMode === "traits_only") {
      const traitPayload = {
        student_id: studentid,
        classroom_id: classid,
        term,
        academic_session: academicSession,
        assessment_mode: "traits_only",
        subjects: [],
        ratings,
        teacher_remarks: teacherRemarks,
        admin_remarks: adminRemarks,
        attendance_count: countVal,
      };

      setGrade.mutate({ payload: traitPayload });
      return;
    }

    const maxCA = caWeight * 100;
    const maxExam = examWeight * 100;

    for (const [subjectId, data] of Object.entries(scores)) {
      const totalCA: any = Object.values(data.ca || {}).reduce(
        (sum: number, val) => sum + Number(val || 0),
        0,
      );
      if (totalCA > maxCA) {
        alert(
          `⚠️ CA (${totalCA}) exceeds ${maxCA} for "${subjects.find((s: any) => s.id === subjectId)?.name}"`,
        );
        return;
      }
      if (data.exam > maxExam) {
        alert(
          `⚠️ Exam (${data.exam}) exceeds ${maxExam} for "${subjects.find((s: any) => s.id === subjectId)?.name}"`,
        );
        return;
      }
    }

    const createGrades = Object.entries(scores).map(
      ([subjectId, data], index) => ({
        subject_id: subjectId,
        ca_scores: Object.fromEntries(
          Object.entries(data.ca || {}).map(([k, v]) => [
            k,
            v === "" ? 0 : Number(v),
          ]),
        ),
        exam_score: data.exam === "" ? 0 : Number(data.exam || 0),
        remarks: data.remark || "",
        ...(index === 0 ? { attendance_count: countVal } : {}),
      }),
    );

    const createPayload = {
      student_id: studentid,
      classroom_id: classid,
      term,
      academic_session: academicSession,
      assessment_mode: "standard_matrix",
      subjects: createGrades,
      ratings,
      teacher_remarks: teacherRemarks,
      admin_remarks: adminRemarks,
      attendance_count: countVal,
    };

    setGrade.mutate({ payload: createPayload });
  };

  const isValidUrl = (url: any) =>
    typeof url === "string" &&
    url.trim() !== "" &&
    url !== "undefined" &&
    url !== "null";

  const studentPhotoUrl = [
    studentPix,
    reportData?.student?.profile_picture,
    reportData?.student?.passport_photo,
    reportData?.student?.image,
    reportData?.student?.photo,
    reportData?.profile_picture,
    reportData?.passport_photo,
  ].find(isValidUrl);

  const resolvedStudentName =
    reportData?.student?.surname && reportData?.student?.firstname
      ? `${reportData.student.surname} ${reportData.student.firstname} ${reportData.student.othername || ""}`.trim()
      : student_name;

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
      {/* Header Panel Navigation */}
      <div className="w-full flex items-center justify-between">
        <BackButton />
        <TopAdminNav name="Admin Manager" title="Performance Management" />
      </div>

      {/* Profile Overview Header Card Layout */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="h-16 w-16 rounded-2xl border-2 border-slate-100 overflow-hidden shadow-sm shrink-0 bg-slate-50 flex items-center justify-center text-slate-400 font-bold uppercase text-lg">
            {studentPhotoUrl ? (
              <img
                src={studentPhotoUrl}
                alt={resolvedStudentName}
                className="w-full h-full object-cover"
              />
            ) : (
              resolvedStudentName?.slice(0, 2)
            )}
          </div>
          <div>
            <h1 className="text-lg font-black text-slate-800 tracking-tight">
              {resolvedStudentName}
            </h1>
            <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5 mt-0.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded font-mono text-[11px] bg-[#0096D1]/5 text-[#0096D1] font-bold">
                ID: {studentid?.slice(-6).toUpperCase()}
              </span>
              • Terminal Grade Worksheet Matrix Entry
            </p>
          </div>
        </div>
        <div className="w-full sm:w-auto shrink-0">
          <RegisterCard
            title="Worksheet Status"
            details="Data entries sync directly with central cloud registry"
          />
        </div>
      </div>

      {/* Assessment Mode Configuration Bar - Locked to Classroom Configuration (No Toggle) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-left">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center shrink-0">
            <FaLayerGroup className="text-sm" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-800">
                Classroom Assessment Mode{selectedClass?.name ? ` (${selectedClass.name})` : ""}:
              </span>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                  assessmentMode === "traits_only"
                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                    : "bg-blue-50 text-blue-700 border border-blue-200"
                }`}
              >
                {assessmentMode === "traits_only"
                  ? "Traits Only Mode"
                  : "Standard Matrix (Full Subjects)"}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">
              {assessmentMode === "traits_only"
                ? "This classroom is configured for observational traits, milestones, and remarks only (no subject exams)."
                : "This classroom is configured for continuous assessment CA points, examination grades, and behavioral metrics."}
            </p>
          </div>
        </div>

        {/* Classroom Mode Status Badge - No Toggle as requested */}
        <div className="flex items-center">
          {assessmentMode === "traits_only" ? (
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-black bg-amber-50 text-amber-800 border border-amber-200 shadow-2xs">
              <FaStar className="text-amber-500 text-[11px]" />
              <span>Traits Only Entry</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-black bg-[#0096D1]/10 text-[#0096D1] border border-[#0096D1]/20 shadow-2xs">
              <FaGraduationCap className="text-[#0096D1] text-[11px]" />
              <span>Full Subject Entry</span>
            </div>
          )}
        </div>
      </div>

      {/* Trait-Only Notice Banner */}
      {assessmentMode === "traits_only" && (
        <div className="bg-sky-50/70 border border-sky-200/80 rounded-2xl p-4 flex items-center gap-3.5 text-sky-800 animate-fade-in text-left">
          <div className="h-9 w-9 rounded-xl bg-[#0096D1] text-white flex items-center justify-center shrink-0 shadow-xs">
            <FaInfoCircle className="text-base" />
          </div>
          <div className="text-xs">
            <p className="font-extrabold text-sky-900 tracking-tight">
              Observational & Trait Assessment Mode Active
            </p>
            <p className="text-sky-700/90 mt-0.5 font-medium leading-relaxed">
              Subject examination and CA tables are hidden for this class profile (e.g. Creche, Nursery, Early Years).
              Rate the character traits, enter attendance, and record observational appraisal remarks below.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Term Allocation Parameters Configuration */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 pl-1">
              <FaCalendarAlt className="text-[#0096D1]" /> Target Session Term
            </label>
            <select
              className="w-full text-sm font-semibold px-4 py-3 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
              value={academicSession}
              onChange={(e) => setAcademicSession(e.target.value)}
              required
            >
              <option value="">Select Academic Session</option>
              {sessionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5 text-left">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 pl-1">
              <FaBookmark className="text-[#0096D1]" /> Terminal Period
              Distribution
            </label>
            <select
              className="w-full text-sm font-semibold px-4 py-3 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all cursor-pointer"
              value={term}
              onChange={(e) => setTerm(Number(e.target.value))}
              required
            >
              <option value="">Select Term Period</option>
              <option value={1}>1st Term Assessment</option>
              <option value={2}>2nd Term Assessment</option>
              <option value={3}>3rd Term Assessment</option>
            </select>
          </div>

          <div className="space-y-1.5 text-left">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 pl-1">
              <FaClipboardList className="text-[#0096D1]" /> Attendance Count
            </label>
            <input
              type="number"
              min="0"
              disabled={isViewOnly}
              placeholder="Enter attendance count..."
              className="w-full text-sm font-semibold px-4 py-3 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all font-mono"
              value={attendanceCount}
              onChange={(e) =>
                setAttendanceCount(
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
            />
          </div>
        </div>

        {/* Subjects Performance Grading Matrix Grid Table (Only in standard mode) */}
        {assessmentMode === "standard" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-fade-in">
            <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center">
                <FaGraduationCap className="text-base" />
              </div>
              <div>
                <h2 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                  Academic Grading Matrix
                </h2>
                <p className="text-[11px] font-medium text-slate-400">
                  Input continuous assessment metrics and standard examination
                  points parameters
                </p>
              </div>
            </div>

            <div className="overflow-x-auto w-full">
              <table className="w-full border-collapse text-left table-fixed min-w-[750px]">
                <thead>
                  <tr className="bg-slate-50/60 border-b border-slate-100 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                    <th className="py-4 px-6 w-[30%]">
                      Registered Subjects Module
                    </th>
                    {[...Array(numCAs)].map((_, i) => (
                      <th key={i} className="py-4 px-2 w-[12%] text-center">
                        CA Point {i + 1}
                      </th>
                    ))}
                    <th className="py-4 px-2 w-[14%] text-center">Exam Bound</th>
                    <th className="py-4 px-2 w-[12%] text-center">Aggregate</th>
                    <th className="py-4 px-6 w-[16%] text-center">
                      Remarks Tracker
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {subjects.map((sub: any) => (
                    <tr
                      key={sub.id}
                      className="group bg-white hover:bg-slate-50/30 transition-colors text-[13px] font-semibold text-slate-600"
                    >
                      <td className="py-3 px-6 font-bold text-slate-800 capitalize tracking-tight">
                        {sub.name}
                      </td>
                      {[...Array(numCAs)].map((_, i) => (
                        <td key={i} className="py-3 px-2 text-center">
                          <input
                            type="number"
                            step="any"
                            disabled={isViewOnly}
                            min="0"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown")
                                e.preventDefault();
                            }}
                            className="w-16 text-center text-sm font-bold px-2 py-1.5 bg-slate-50/50 rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:bg-white focus:border-[#0096D1] disabled:opacity-50 disabled:bg-slate-100/70 transition-all font-mono"
                            value={scores[sub.id]?.ca?.[i + 1] ?? ""}
                            onChange={(e) =>
                              handleCAScoreChange(sub.id, i + 1, e.target.value)
                            }
                          />
                        </td>
                      ))}
                      <td className="py-3 px-2 text-center">
                        <input
                          type="number"
                          step="any"
                          disabled={isViewOnly}
                          min="0"
                          onWheel={(e) => e.currentTarget.blur()}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowUp" || e.key === "ArrowDown")
                              e.preventDefault();
                          }}
                          className="w-20 text-center text-sm font-bold px-2 py-1.5 bg-slate-50/50 rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:bg-white focus:border-[#0096D1] disabled:opacity-50 disabled:bg-slate-100/70 transition-all font-mono"
                          value={scores[sub.id]?.exam ?? ""}
                          onChange={(e) =>
                            handleExamChange(sub.id, e.target.value)
                          }
                        />
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md font-mono font-black bg-blue-50 text-blue-600 text-xs shadow-inner">
                          {calcTotal(sub.id)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-center">
                        <button
                          type="button"
                          onClick={() =>
                            setRemarkModal({ open: true, subject: sub.id })
                          }
                          className={`inline-flex items-center gap-1 text-xs font-bold transition-all ${scores[sub.id]?.remark
                            ? "text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg"
                            : "text-[#0096D1] hover:text-[#007ba8] hover:underline"
                            }`}
                        >
                          <FaPenNib className="text-[10px]" />{" "}
                          {scores[sub.id]?.remark ? "Edit Remark" : "Add Remark"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Effective Areas & Psychomotor Rating Configuration Grid Block Layout */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4 text-left">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-50 pb-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <FaStar className="text-base" />
              </div>
              <div>
                <h3 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                  {assessmentMode === "traits_only"
                    ? "Observational & Character Traits Evaluation"
                    : "Psychomotor Skills & Behavioral Metrics"}
                </h3>
                <p className="text-[11px] font-medium text-slate-400">
                  Assess developmental milestones and behavioural indices on a 1 to 5 scale
                </p>
              </div>
            </div>

            {/* Quick Fill Shortcuts */}
            <div className="flex items-center gap-1.5 self-end sm:self-auto">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 hidden sm:inline">
                Quick Fill:
              </span>
              <button
                type="button"
                disabled={isViewOnly}
                onClick={() => handleSetAllRatings(5)}
                className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
              >
                All 5★
              </button>
              <button
                type="button"
                disabled={isViewOnly}
                onClick={() => handleSetAllRatings(4)}
                className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                All 4★
              </button>
              <button
                type="button"
                disabled={isViewOnly}
                onClick={() => handleSetAllRatings(0)}
                className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ratingTraits?.map((trait: any, idx: number) => {
              const currentRating = ratings[trait.name] || 0;
              return (
                <div
                  key={idx}
                  className="p-3.5 bg-slate-50/50 rounded-xl border border-slate-100 flex flex-col justify-between gap-3"
                >
                  <span className="text-xs font-bold text-slate-700 capitalize tracking-tight">
                    {trait.name}
                  </span>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        key={num}
                        type="button"
                        disabled={isViewOnly}
                        onClick={() => handleRatingChange(trait.name, num)}
                        className={`flex-1 text-xs font-mono font-black py-1.5 rounded-lg border transition-all ${currentRating === num
                          ? "bg-[#0096D1] border-[#0096D1] text-white shadow-sm scale-105"
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:hover:bg-white disabled:hover:text-slate-400"
                          }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dynamic Context Evaluator Textarea Comments Module Blocks */}
        <div className="space-y-6 text-left w-full">
          {user?.role === "teacher" && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-2 w-full">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 pl-1">
                <FaCommentMedical className="text-[#0096D1]" /> Teacher’s
                Appraisal Summary Remark
              </label>
              <textarea
                rows={6}
                className="w-full min-h-[150px] text-sm font-medium p-4 bg-slate-50 text-slate-700 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
                value={teacherRemarks}
                onChange={(e) => setTeacherRemarks(e.target.value)}
                placeholder="Type structural behavioral evaluation performance updates..."
              />
            </div>
          )}

          {(user?.role === "super_admin" || user?.role === "admin") && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-2 w-full">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 pl-1">
                <FaCommentMedical className="text-purple-600" /> Administrative
                Core Review Endorsement
              </label>
              <textarea
                rows={6}
                className="w-full min-h-[150px] text-sm font-medium p-4 bg-slate-50 text-slate-700 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-purple-500 transition-all"
                value={adminRemarks}
                onChange={(e) => setAdminRemarks(e.target.value)}
                placeholder="Provide terminal verification signature details or administrative parameters overrides..."
              />
            </div>
          )}
        </div>

        {/* Action Form Confirmation Controls Area Footer panel row */}
        <div className="flex justify-end pt-2">
          <button
            type="submit"
            className="inline-flex items-center justify-center min-w-[160px] h-11 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transform active:scale-95 transition-all"
          >
            {setGrade.isPending || updateReport.isPending ? (
              <CircleLoader />
            ) : (
              "Save Report"
            )}
          </button>
        </div>
      </form>

      {/* Subject Metric Remark Textarea Overlay Modal Dialog Canvas */}
      {remarkModal.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl p-6 w-full max-w-md text-left space-y-4 transform scale-100 transition-transform">
            <div>
              <span className="text-[10px] font-bold text-[#0096D1] uppercase tracking-wider bg-[#0096D1]/5 px-2.5 py-1 rounded-md">
                Subject Assessment Override
              </span>
              <h3 className="font-extrabold text-slate-800 text-base tracking-tight mt-2">
                Performance Remark for:{" "}
                {subjects.find((s: any) => s.id === remarkModal.subject)?.name}
              </h3>
            </div>

            <textarea
              rows={4}
              className="w-full text-sm font-medium p-3 bg-slate-50 text-slate-700 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] transition-all"
              defaultValue={scores[remarkModal.subject!]?.remark || ""}
              id="remarkInput"
              disabled={isViewOnly}
              placeholder="e.g., Demonstrated excellent analytical capability throughout the academic review period..."
            ></textarea>

            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setRemarkModal({ open: false, subject: null })}
                className="rounded-xl px-4 py-2 bg-slate-50 border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-100 transition-all"
              >
                Dismiss
              </button>
              {!isViewOnly && (
                <button
                  type="button"
                  onClick={() =>
                    handleRemarkSave(
                      remarkModal.subject!,
                      (
                        document.getElementById(
                          "remarkInput",
                        ) as HTMLTextAreaElement
                      ).value,
                    )
                  }
                  className="rounded-xl px-5 py-2 bg-[#0096D1] text-white text-xs font-bold hover:bg-[#007ba8] shadow-md transition-all active:scale-95"
                >
                  Save Remark Text
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
